/**
 * SEO Indexing API Service
 *
 * Manages URL submissions to Google Indexing API and IndexNow.
 */

import { callEdgeFunction } from './supabase';
import { supabase } from './supabase';

const SITE_URL = 'https://linkedbysix.com';

// Types
export interface GoogleIndexingStatus {
  configured: boolean;
  enabled: boolean;
  quota_used_today: number;
  quota_remaining: number;
  daily_quota: number;
}

export interface IndexingResult {
  url: string;
  success: boolean;
  error?: string;
}

export interface GoogleIndexingResponse {
  success: boolean;
  submitted: number;
  failed: number;
  quota_used_today: number;
  quota_remaining: number;
  results: IndexingResult[];
  error?: string;
}

export interface IndexNowResponse {
  success: boolean;
  total_submitted: number;
  total_failed: number;
  results: Array<{
    engine: string;
    success: boolean;
    status?: number;
    error?: string;
  }>;
  error?: string;
}

export interface BulkIndexStats {
  published_posts: number;
  active_categories: number;
  static_pages: number;
  total_indexable: number;
}

export interface BulkIndexResponse {
  success: boolean;
  total_urls: number;
  urls: string[];
  google_result?: {
    service: string;
    success: boolean;
    submitted?: number;
    failed?: number;
    error?: string;
  };
  indexnow_result?: {
    service: string;
    success: boolean;
    submitted?: number;
    failed?: number;
    error?: string;
  };
  error?: string;
}

export interface IndexingLogEntry {
  id: string;
  url: string;
  action: 'URL_UPDATED' | 'URL_DELETED';
  service: string;
  status: 'pending' | 'submitted' | 'success' | 'failed';
  response_code?: number;
  response_message?: string;
  blog_post_id?: string;
  submitted_at: string;
  // Enriched from seo_url_inspection join
  google_verdict?: string | null;
  google_coverage_state?: string | null;
  last_inspected_at?: string | null;
}

export interface UrlInspectionResult {
  id: string;
  url: string;
  verdict: 'PASS' | 'PARTIAL' | 'FAIL' | 'NEUTRAL' | 'VERDICT_UNSPECIFIED';
  coverage_state: string | null;
  indexing_state: string | null;
  robots_txt_state: string | null;
  page_fetch_state: string | null;
  google_canonical: string | null;
  user_canonical: string | null;
  last_crawl_time: string | null;
  crawled_as: string | null;
  referring_urls: string[] | null;
  sitemaps: string[] | null;
  inspection_result_link: string | null;
  url_type: 'blog' | 'static' | 'category' | 'other';
  inspected_at: string;
  blog_post_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface InspectionSummary {
  total_inspected: number;
  indexed: number;
  not_indexed: number;
  partially_indexed: number;
  unknown: number;
  never_inspected: number;
  last_inspection_run: string | null;
  by_type: Record<string, { indexed: number; not_indexed: number; total: number }> | null;
  top_coverage_states: Array<{ coverage_state: string; count: number }> | null;
}

export interface UrlInspectionApiStatus {
  configured: boolean;
  quota_used_today: number;
  quota_remaining: number;
  daily_quota: number;
}

// API Functions

/**
 * Check Google Indexing API status and quota
 */
export async function getGoogleIndexingStatus(): Promise<GoogleIndexingStatus> {
  return callEdgeFunction<GoogleIndexingStatus>('google-indexing-api', {
    action: 'status',
  });
}

/**
 * Submit URLs to Google Indexing API
 */
export async function submitToGoogle(
  urls: string[],
  action: 'URL_UPDATED' | 'URL_DELETED' = 'URL_UPDATED'
): Promise<GoogleIndexingResponse> {
  return callEdgeFunction<GoogleIndexingResponse>('google-indexing-api', {
    action,
    urls,
  });
}

/**
 * Submit URLs to IndexNow (Bing, Yandex, etc.)
 */
export async function submitToIndexNow(urls: string[]): Promise<IndexNowResponse> {
  return callEdgeFunction<IndexNowResponse>('indexnow-submit', {
    urls,
  });
}

/**
 * Get bulk indexing site stats
 */
export async function getBulkIndexStats(): Promise<BulkIndexStats & { static_urls: string[] }> {
  return callEdgeFunction<{ stats: BulkIndexStats; static_urls: string[] }>('bulk-index-site', {
    type: 'status',
  }).then((res) => ({ ...res.stats, static_urls: res.static_urls }));
}

/**
 * Bulk index site URLs
 */
export async function bulkIndexSite(
  type: 'all' | 'static' | 'blogs' | 'categories' | 'urls',
  options?: {
    urls?: string[];
    action?: 'URL_UPDATED' | 'URL_DELETED';
    limit?: number;
    indexnow_only?: boolean;
    google_only?: boolean;
  }
): Promise<BulkIndexResponse> {
  return callEdgeFunction<BulkIndexResponse>('bulk-index-site', {
    type,
    ...options,
  });
}

/**
 * Crawl seed URLs used for bulk discovery (crawl hubs), not "Request Indexing".
 */
export async function getCrawlSeedUrls(): Promise<string[]> {
  const { data, error } = await supabase
    .from('blog_categories')
    .select('slug')
    .eq('is_active', true);

  if (error) throw error;

  const categoryUrls = (data || [])
    .map((c: { slug: string }) => c.slug)
    .filter(Boolean)
    .map((slug: string) => `${SITE_URL}/blog/category/${slug}`);

  const urls = [
    `${SITE_URL}/sitemap.xml`,
    `${SITE_URL}/blog`,
    `${SITE_URL}/blog/archive`,
    ...categoryUrls,
  ];

  return [...new Set(urls)];
}

/**
 * Submit crawl seed URLs (primarily for IndexNow; Google discovery comes from crawl + sitemap).
 */
export async function submitCrawlSeeds(): Promise<BulkIndexResponse> {
  const urls = await getCrawlSeedUrls();
  // Avoid burning Google Indexing API quota on hubs; IndexNow is the safer bulk signal.
  return bulkIndexSite('urls', { urls, indexnow_only: true });
}

/**
 * Submit a single URL to all search engines
 */
export async function submitUrl(
  url: string,
  action: 'URL_UPDATED' | 'URL_DELETED' = 'URL_UPDATED'
): Promise<{ google: GoogleIndexingResponse; indexnow: IndexNowResponse }> {
  const [google, indexnow] = await Promise.all([
    submitToGoogle([url], action),
    submitToIndexNow([url]),
  ]);
  return { google, indexnow };
}

/**
 * Get recent indexing log entries, enriched with actual Google indexing status
 */
export async function getIndexingLog(
  limit: number = 50,
  offset: number = 0
): Promise<{ data: IndexingLogEntry[]; count: number }> {
  const { data: logEntries, error, count } = await supabase
    .from('seo_indexing_log')
    .select('*', { count: 'exact' })
    .order('submitted_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    throw error;
  }

  // Enrich with inspection status for these URLs
  const urls = [...new Set((logEntries || []).map((e) => e.url))];
  if (urls.length > 0) {
    const { data: inspections } = await supabase
      .from('seo_url_inspection')
      .select('url, verdict, coverage_state, inspected_at')
      .in('url', urls);

    const inspectionMap = new Map(
      (inspections || []).map((i) => [i.url, i])
    );

    const enrichedData = (logEntries || []).map((entry) => ({
      ...entry,
      google_verdict: inspectionMap.get(entry.url)?.verdict || null,
      google_coverage_state: inspectionMap.get(entry.url)?.coverage_state || null,
      last_inspected_at: inspectionMap.get(entry.url)?.inspected_at || null,
    }));

    return { data: enrichedData, count: count || 0 };
  }

  return { data: logEntries || [], count: count || 0 };
}

/**
 * Get indexing stats for a specific day
 */
export async function getDailyIndexingStats(date?: Date): Promise<{
  google: { submitted: number; failed: number };
  indexnow: { submitted: number; failed: number };
}> {
  const targetDate = date || new Date();
  const dateStr = targetDate.toISOString().split('T')[0];

  const { data, error } = await supabase
    .from('seo_indexing_log')
    .select('service, status')
    .gte('submitted_at', `${dateStr}T00:00:00Z`)
    .lt('submitted_at', `${dateStr}T23:59:59Z`);

  if (error) {
    throw error;
  }

  const stats = {
    google: { submitted: 0, failed: 0 },
    indexnow: { submitted: 0, failed: 0 },
  };

  for (const entry of data || []) {
    const serviceKey = entry.service.startsWith('indexnow') ? 'indexnow' : 'google';
    if (entry.status === 'success') {
      stats[serviceKey].submitted++;
    } else if (entry.status === 'failed') {
      stats[serviceKey].failed++;
    }
  }

  return stats;
}

// ---- URL Inspection API Functions ----

/**
 * Check URL Inspection API configuration and quota
 */
export async function getUrlInspectionStatus(): Promise<UrlInspectionApiStatus> {
  return callEdgeFunction<UrlInspectionApiStatus>('google-url-inspection', {
    action: 'status',
  });
}

/**
 * Inspect a single URL to check its actual Google indexing status
 */
export async function inspectSingleUrl(url: string): Promise<{
  success: boolean;
  url: string;
  inspection: UrlInspectionResult | null;
  error?: string;
}> {
  return callEdgeFunction('google-url-inspection', {
    action: 'inspect',
    url,
  });
}

/**
 * Batch inspect multiple URLs
 */
export async function batchInspectUrls(
  urls: string[],
  limit?: number
): Promise<{
  success: boolean;
  total: number;
  inspected: number;
  failed: number;
  timed_out?: boolean;
  elapsed_seconds?: number;
  results: Array<{
    url: string;
    success: boolean;
    verdict?: string;
    coverage_state?: string;
    error?: string;
  }>;
  quota_used_today: number;
  quota_remaining: number;
}> {
  return callEdgeFunction('google-url-inspection', {
    action: 'batch_inspect',
    urls,
    limit,
  });
}

/**
 * Get inspection summary stats (indexed/not_indexed/never_inspected counts)
 */
export async function getInspectionSummary(): Promise<InspectionSummary> {
  const { data, error } = await supabase.rpc('get_inspection_summary');
  if (error) throw error;
  return data;
}

/**
 * Get inspection results with filtering and pagination
 */
export async function getInspectionResults(options: {
  verdict?: string;
  url_type?: string;
  limit?: number;
  offset?: number;
}): Promise<{ data: UrlInspectionResult[]; count: number }> {
  let query = supabase
    .from('seo_url_inspection')
    .select('*', { count: 'exact' })
    .order('inspected_at', { ascending: false });

  if (options.verdict) {
    if (options.verdict === 'NOT_INDEXED') {
      query = query.in('verdict', ['FAIL', 'NEUTRAL']);
    } else {
      query = query.eq('verdict', options.verdict);
    }
  }
  if (options.url_type) query = query.eq('url_type', options.url_type);

  const limit = options.limit || 50;
  const offset = options.offset || 0;
  query = query.range(offset, offset + limit - 1);

  const { data, error, count } = await query;
  if (error) throw error;
  return { data: data || [], count: count || 0 };
}

/**
 * Get URLs that need inspection (never inspected or stale)
 */
export async function getUrlsNeedingInspection(
  maxCount: number = 100
): Promise<Array<{ url: string; url_type: string; blog_post_id: string | null; last_inspected: string | null }>> {
  const { data, error } = await supabase.rpc('get_urls_needing_inspection', {
    max_count: maxCount,
    min_age_hours: 24,
  });
  if (error) throw error;
  return data || [];
}

// ---- Content Health & Cannibalization Functions ----

export interface CannibalizationGroup {
  group_id: number;
  post_id: string;
  title: string;
  slug: string;
  cluster: string;
  primary_keyword: string;
  strength_score: number;
  google_verdict: string | null;
  similarity_to_strongest: number;
}

export interface ClusterSaturation {
  cluster: string;
  post_count: number;
  saturation_level: string;
}

export interface SubmissionTrackerEntry {
  url: string;
  total_submissions: number;
  last_submitted_at: string | null;
  last_verdict: string | null;
  last_verdict_at: string | null;
  fail_reason: string | null;
  hold_until: string | null;
  permanently_held: boolean;
  hold_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubmissionHealthEntry {
  url: string;
  total_submissions: number;
  last_submitted_at: string | null;
  last_verdict: string | null;
  fail_reason: string | null;
  hold_until: string | null;
  permanently_held: boolean;
  hold_reason: string | null;
  current_verdict: string | null;
  coverage_state: string | null;
  title: string | null;
  cluster: string | null;
  submission_status: string;
}

export interface SubmissionHealthSummary {
  total: number;
  indexed: number;
  never_submitted: number;
  in_cooldown: number;
  eligible: number;
  permanently_held: number;
  max_attempts: number;
  by_fail_reason: Record<string, number>;
}

/**
 * Get cannibalization groups detected by title similarity
 */
export async function getCannibalizationGroups(options?: {
  cluster?: string;
  limit?: number;
}): Promise<CannibalizationGroup[]> {
  const { data, error } = await supabase.rpc('detect_cannibalization_groups', {
    similarity_threshold: 0.3,
    min_group_size: 2,
  });
  if (error) throw error;

  let groups = data || [];
  if (options?.cluster) {
    groups = groups.filter((g: CannibalizationGroup) => g.cluster === options.cluster);
  }
  return groups;
}

/**
 * Get cluster saturation levels (post counts per cluster)
 */
export async function getClusterSaturation(): Promise<ClusterSaturation[]> {
  const { data, error } = await supabase.rpc('get_cluster_saturation');
  if (error) throw error;
  return data || [];
}

/**
 * Get submission health data from the v_submission_health view
 */
export async function getSubmissionHealth(options?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ data: SubmissionHealthEntry[]; count: number }> {
  let query = supabase
    .from('v_submission_health')
    .select('*', { count: 'exact' })
    .order('last_submitted_at', { ascending: false, nullsFirst: false });

  if (options?.status) {
    query = query.eq('submission_status', options.status);
  }

  const limit = options?.limit || 50;
  const offset = options?.offset || 0;
  query = query.range(offset, offset + limit - 1);

  const { data, error, count } = await query;
  if (error) throw error;
  return { data: data || [], count: count || 0 };
}

/**
 * Get a summary of submission health across all URLs
 */
export async function getSubmissionHealthSummary(): Promise<SubmissionHealthSummary> {
  const { data, error } = await supabase
    .from('v_submission_health')
    .select('submission_status, fail_reason');

  if (error) throw error;

  const entries = data || [];
  const summary: SubmissionHealthSummary = {
    total: entries.length,
    indexed: 0,
    never_submitted: 0,
    in_cooldown: 0,
    eligible: 0,
    permanently_held: 0,
    max_attempts: 0,
    by_fail_reason: {},
  };

  for (const entry of entries) {
    const status = entry.submission_status as string;
    if (status === 'indexed') summary.indexed++;
    else if (status === 'never_submitted') summary.never_submitted++;
    else if (status === 'in_cooldown') summary.in_cooldown++;
    else if (status === 'eligible') summary.eligible++;
    else if (status === 'permanently_held') summary.permanently_held++;
    else if (status === 'max_attempts') summary.max_attempts++;

    if (entry.fail_reason) {
      summary.by_fail_reason[entry.fail_reason] =
        (summary.by_fail_reason[entry.fail_reason] || 0) + 1;
    }
  }

  return summary;
}

/**
 * Release a permanently held URL (clear hold, reset cooldown)
 */
export async function releaseHeldUrl(url: string): Promise<void> {
  const { error } = await supabase
    .from('seo_submission_tracker')
    .update({
      permanently_held: false,
      hold_reason: null,
      hold_until: null,
      updated_at: new Date().toISOString(),
    })
    .eq('url', url);

  if (error) throw error;
}

/**
 * Permanently hold a URL (prevent future submissions)
 */
export async function holdUrl(url: string, reason: string): Promise<void> {
  const { error } = await supabase
    .from('seo_submission_tracker')
    .upsert({
      url,
      permanently_held: true,
      hold_reason: reason,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'url' });

  if (error) throw error;
}

/**
 * Archive a blog post (sets status to 'archived')
 */
export async function archivePost(postId: string): Promise<void> {
  const { error } = await supabase
    .from('blog_posts')
    .update({ status: 'archived' })
    .eq('id', postId);

  if (error) throw error;
}

/**
 * Calculate page strength score for a specific post
 */
export async function getPageStrength(postId: string): Promise<number> {
  const { data, error } = await supabase.rpc('calculate_page_strength', {
    p_post_id: postId,
  });
  if (error) throw error;
  return data || 0;
}

/**
 * Run the fail-reason analysis on submission tracker
 */
export async function runVerdictAnalysis(): Promise<{
  updated: number;
  newly_held: number;
  fail_reasons: Record<string, number>;
}> {
  const { data, error } = await supabase.rpc('analyze_and_update_submission_tracker');
  if (error) throw error;
  return data || { updated: 0, newly_held: 0, fail_reasons: {} };
}

// ---- Manual Indexing Queue ----

export interface ManualIndexingQueueEntry {
  url: string;
  verdict: string | null;
  coverage_state: string | null;
  url_type: string;
  title: string | null;
  total_submissions: number;
  last_submitted_at: string | null;
  inspected_at: string | null;
  strength_score: number | null;
}

/**
 * Get priority queue of URLs that need manual "Request Indexing" via Search Console.
 * These are URLs that have been API-submitted but aren't indexed, ordered by strength.
 */
export async function getManualIndexingQueue(): Promise<ManualIndexingQueueEntry[]> {
  const { data, error } = await supabase.rpc('get_manual_indexing_queue');
  if (error) throw error;
  return data || [];
}

/**
 * Mark a URL as manually submitted via Search Console
 */
export async function markManuallySubmitted(url: string): Promise<void> {
  const { error } = await supabase
    .from('seo_submission_tracker')
    .upsert({
      url,
      last_submitted_at: new Date().toISOString(),
      hold_reason: 'manual_search_console',
      hold_until: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7-day cooldown
      updated_at: new Date().toISOString(),
    }, { onConflict: 'url' });

  if (error) throw error;
}
